import { useCallback, useRef } from 'react';
import { IsAttentionItem, } from '@crew/apis/attention/models/getAttentions/response';
import { IsDeletedAttentionItem, } from '@crew/apis/websocket/models/deletedAttentionItem/deletedAttentionItem';
import { IsDeletedFeedItem, } from '@crew/apis/websocket/models/deletedFeedItem/deletedFeedItem';
import { IsUpdatedUserPresence, } from '@crew/apis/websocket/models/updatedUserPresence/updatedUserPresence';
import { isAnsweredCall, } from '@crew/apis/websocket/models/answeredCall/answeredCall';
import { IsUpdatedAiCredit, } from '@crew/apis/websocket/models/updatedAiCredit/updatedAiCredit';
import { IsUpdatedMeetingRecordingStreamState, } from '@crew/apis/websocket/models/updatedMeetingRecordingStreamState/updatedMeetingRecordingStreamState';
import { IsRelatedSuggestion } from '@crew/models/domain';
import { PubSubEventType, UnreadInfoTarget, PubSubEventDataType, PubSubEventTargetType, PubSubCallEventType, } from '@crew/enums/domain';
import { IsChatMessage, IsChatReaction, IsDirectChannel, IsUpdateUnreadInfo, IsAnnouncement, } from '@crew/models/domain';
import { useAttentionService, useChatMessageService, useBookmarkService, useFeedService, useUnreadService, useActiveChatRoomService, useAnnouncementService, } from '@crew/states';
import { useWebsocket } from './useWebsocket';
import { assertNever } from '@crew/utils';
// 各AsyncMessageTypeに対応したwebSocketのメッセージリスナーを定義したcustom hooks
/**
 * フィードに関連するメッセージのsubscribeを行うcustom hook
 * @param onRecvAddMessage
 * @param onRecvDeleteMessage
 * @param dispatch
 */
export const useSubscribeFeed = (onRecvAddMessage, dispatch, selector) => {
    // メッセージ追加
    useSubscribeFeedChatMessageAdded(onRecvAddMessage, dispatch, selector);
    // メッセージ更新
    useSubscribeFeedChatMessageUpdated(dispatch, selector);
    // メッセージ削除
    useSubscribeFeedChatMessageDeleted(dispatch, selector);
    // リアクション追加
    useSubscribeFeedReactionAdded(dispatch, selector);
    // リアクション削除
    useSubscribeFeedReactionDeleted(dispatch, selector);
    // ブックマーク追加
    useSubscribeBookmarked(dispatch, selector);
    // ブックマーク削除
    useSubscribeDropBookmark(dispatch, selector);
};
/**
 * アテンションに関連するメッセージのsubscribeを行うcustom hook
 * @param onRecvAddOrUpdateMessage
 * @param onRecvDeleteMessage
 * @param dispatch
 */
export const useSubscribeAttention = (onRecvAddMessage, dispatch, selector) => {
    // アテンション追加
    useSubscribeAttentionItemAdded(onRecvAddMessage, dispatch, selector);
    // アテンション削除
    useSubscribeAttentionItemDeleted(dispatch, selector);
    // ブックマーク追加
    useSubscribeBookmarked(dispatch, selector);
    // ブックマーク削除
    useSubscribeDropBookmark(dispatch, selector);
    // リアクション追加
    useSubscribeFeedReactionAdded(dispatch, selector);
    // リアクション削除
    useSubscribeFeedReactionDeleted(dispatch, selector);
};
/**
 * ブックマーク関連のメッセージのsubscribeを行うcustom hook
 * @param onRecvUpdateMessage
 * @param onRecvDeleteMessage
 * @param dispatch
 */
export const useSubscribeBookmark = (dispatch, selector) => {
    // ブックマーク追加
    useSubscribeBookmarked(dispatch, selector);
    // ブックマーク削除
    useSubscribeDropBookmark(dispatch, selector);
    // メッセージ更新
    useSubscribeFeedChatMessageUpdated(dispatch, selector);
    // メッセージ削除
    useSubscribeFeedChatMessageDeleted(dispatch, selector);
    // リアクション追加
    useSubscribeFeedReactionAdded(dispatch, selector);
    // リアクション削除
    useSubscribeFeedReactionDeleted(dispatch, selector);
};
/**
 * ダイレクトメッセージに必要なメッセージのsubscribeを行うcustom hook
 * @param onRecvDirectChannelAdded
 * @param dispatch
 */
export const useSubscribeDirectChannel = (onRecvDirectChannelAdded, dispatch, selector) => {
    // ダイレクトチャンネル追加
    useSubscribeDirectChannelAdded(onRecvDirectChannelAdded, dispatch, selector);
};
/**
 * チャットに関連するメッセージのsubscribeを行うcustom hook
 * @param targetId
 * @param onRecvMessageAdded
 * @param dispatch
 */
export const useSubscribeChat = (targetId, onRecvMessageAdded, dispatch, selector) => {
    // メッセージ追加
    useSubscribeChatMessageAdded(targetId, onRecvMessageAdded, dispatch, selector);
    // メッセージ更新
    useSubscribeChatMessageDeleted(targetId, dispatch, selector);
    // メッセージ削除
    useSubscribeChatMessageUpdated(targetId, dispatch, selector);
    // リアクション追加
    useSubscribeChatReactionAdded(targetId, dispatch, selector);
    // リアクション削除
    useSubscribeChatReactionDeleted(targetId, dispatch, selector);
    // ブックマーク追加
    useSubscribeBookmarked(dispatch, selector);
    // ブックマーク削除
    useSubscribeDropBookmark(dispatch, selector);
};
/**
 * ユーザー設定に関連するメッセージのsubscribeを行うcustom hook
 * @param dispatch reduxのdispatch
 * @param selector reduxのuseSelector
 */
export const useSubscribeUserSetting = (dispatch, selector) => {
    // チャットルーム全既読処理受信用
    useSubscribeUserUnreadAllChatRead(dispatch, selector);
};
/**
 * 未読関連のメッセージのsubscribeを行うcustom hook
 */
export const useSubscribeUnread = () => {
    // 未読情報更新
    useSubscribeUnreadUpdated();
    // 「すべて既読にする」契機での未読情報更新
    useSubscribeUnreadChatRoomAllRead();
};
/**
 * ユーザー在席状態関連のメッセージのsubscribeを行うcustom hook
 */
export const useSubscribeUserPresenceState = (targetUserId, onRecvUserStateUpdated) => {
    // ユーザー在籍状態更新
    useSubscribeUserPresenceUpdated(targetUserId, onRecvUserStateUpdated);
};
/**
 * お知らせ関連のメッセージのsubscribeを行うcustom hook
 */
export const useSubscribeAnnouncement = () => {
    // お知らせ追加
    useSubscribeAnnouncementAdded();
    // お知らせ削除
    useSubscribeAnnouncementDeleted();
    // お知らせ既読
    useSubscribeAnnouncementRead();
    // お知らせ全既読
    useSubscribeAnnouncementAllRead();
};
/**
 * 電話に関連するメッセージのsubscribeを行うcustom hook
 * @param callId
 */
export const useSubscribeCall = (callId, handleRecvEvent) => {
    // handleRecvEventの変更での再レンダリングを防ぐため、useRefでラップする
    const handleRecvEventRef = useRef(handleRecvEvent);
    handleRecvEventRef.current = handleRecvEvent;
    // イベントハンドラ(ペイロードなし汎用)
    const handleRecvGenericMessage = useCallback((type) => {
        console.log(`[subscribeMessage] Recv ${type}`);
        handleRecvEventRef.current({ type });
    }, []);
    // 相手が電話に出たイベントハンドラ
    const handleRecvMessage = useCallback((type, payload) => {
        console.log(`[subscribeMessage] Recv ${type} payload:`, payload);
        handleRecvEventRef.current(Object.assign({ type }, payload));
    }, []);
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallAnswered,
        IsValidMessage: isAnsweredCall,
        onRecvMessage: handleRecvMessage,
    }, Boolean(callId));
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallBeginVideo,
        IsValidMessage: true,
        onRecvMessage: handleRecvGenericMessage,
    }, Boolean(callId));
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallCancel,
        IsValidMessage: true,
        onRecvMessage: handleRecvGenericMessage,
    }, Boolean(callId));
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallDeclined,
        IsValidMessage: true,
        onRecvMessage: handleRecvGenericMessage,
    }, Boolean(callId));
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallEnded,
        IsValidMessage: true,
        onRecvMessage: handleRecvGenericMessage,
    }, Boolean(callId));
    useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Call,
        targetId: callId,
        messageTypeFilter: PubSubCallEventType.CallFailed,
        IsValidMessage: true,
        onRecvMessage: handleRecvGenericMessage,
    }, Boolean(callId));
};
/**
 * 議事録に関連するメッセージのsubscribeを行うcustom hook
 * @param eventId
 */
export const useSubscribeMeetingMinutes = (eventId, onRecvMeetingMinutesProcessing, onRecvMeetingMinutesCreated, onRecvMeetingMinutesGenerationFailed, onRecvMeetingMinutesGenerationTokenLimitExceeded) => {
    useSubscribeMeetingMinutesProcessing(eventId, onRecvMeetingMinutesProcessing);
    useSubscribeMeetingMinutesCreated(eventId, onRecvMeetingMinutesCreated);
    useSubscribeMeetingMinutesGenerationFailed(eventId, onRecvMeetingMinutesGenerationFailed);
    useSubscribeMeetingMinutesGenerationTokenLimitExceeded(eventId, onRecvMeetingMinutesGenerationTokenLimitExceeded);
};
/**
 * タスクの関連情報作成をsubscribeするcustom hook
 * @param onRecvTaskRelatedInfoCreated
 */
export const useSubscribeTaskRelatedInfo = (onRecvTaskRelatedInfoCreated, onRecvTaskRelatedInfoFailed) => {
    useSubscribeTaskRelatedSuggestionCreated(onRecvTaskRelatedInfoCreated);
    useSubscribeTaskRelatedSuggestionFailed(onRecvTaskRelatedInfoFailed);
};
/**
 * 会議の関連情報作成をsubscribeするcustom hook
 * @param onRecvEventRelatedInfoCreated
 */
export const useSubscribeEventRelatedInfo = (onRecvEventRelatedInfoCreated, onRecvEventRelatedInfoFailed) => {
    useSubscribeEventRelatedSuggestionCreated(onRecvEventRelatedInfoCreated);
    useSubscribeEventRelatedSuggestionFailed(onRecvEventRelatedInfoFailed);
};
// =================================================================================
// 以下はメッセージ単位のサブスクライブを行うcustom hooks
// 各コンポーネントからは直接呼び出さないため、exportは行わない
// =================================================================================
/*====== feed_reaction ======================*/
/**
 * メッセージのリアクション追加をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeFeedReactionAdded = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv add reaction via websocket. ${recv.chatMessageId}`);
        // リアクション情報を追加
        chatMessageService.insertReaction({ reaction: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.ChatMessageReactionAdded,
        IsValidMessage: IsChatReaction,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue,
    );
};
/**
 * メッセージのリアクション削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeFeedReactionDeleted = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv delete reaction via websocket. ${recv.chatMessageId}`);
        // リアクション情報の削除
        chatMessageService.deleteReaction({ reaction: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.ChatMessageReactionDeleted,
        IsValidMessage: IsChatReaction,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== feed ==========================================*/
/**
 * メッセージの追加をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeFeedChatMessageAdded = (onRecvMessage, dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // Sliceの操作を行うためのServiceを取得
    const feedService = useFeedService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv message via websocket. ${recv.id}`);
        // ChatMessageSliceにメッセージを追加
        chatMessageService.addChatMessageToCache({
            chatMessage: recv,
        });
        // FeedのViewModelにもメッセージを追加
        feedService.addFeedLatestMessage({
            chatMessage: recv,
        });
        // コールバック関数を実行。現在は既読情報の更新のみ行っている
        onRecvMessage === null || onRecvMessage === void 0 ? void 0 : onRecvMessage(recv);
    }, [feedService, chatMessageService, onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.FeedItemAdded,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * メッセージの更新をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeFeedChatMessageUpdated = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // メッセージを更新
        chatMessageService.setChatMessage({ chatMessage: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.FeedItemUpdated,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * メッセージの削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeFeedChatMessageDeleted = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // Sliceの操作を行うためのServiceを取得
    const feedService = useFeedService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // メッセージを削除
        chatMessageService.deleteChatMessage({
            chatMessageId: recv.chatMessageId,
            parentChatMessageId: recv.parentChatMessageId,
            chatRoomId: recv.chatRoomId,
            parentChatRoomId: recv.parentChatRoomId,
        });
        // FeedのViewModelからもメッセージを削除
        feedService.deleteFeedMessage({
            chatMessageId: recv.chatMessageId,
        });
    }, [chatMessageService, feedService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.FeedItemDeleted,
        IsValidMessage: IsDeletedFeedItem,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== bookmark =====================================/

/**
 * メッセージがブックマークされたかをsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeBookmarked = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    const bookmarkService = useBookmarkService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv bookmark message via websocket. ${recv.id}`);
        // キャッシュ済みのチャットメッセージを受け取ったブックマーク済みの情報に置き換え
        chatMessageService.setChatMessage({ chatMessage: recv });
        // BookmarkのViewModelにもメッセージを追加
        bookmarkService.addBookmarkMessage({
            chatMessageId: recv.id,
            archived: recv.bookmarks[0].archived,
            createdAt: recv.createdAt,
        });
    }, [bookmarkService, chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.BookmarkAdded,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * メッセージのブックマーク削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeDropBookmark = (dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    const bookmarkService = useBookmarkService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv bookmark message via websocket. ${recv.id}`);
        // キャッシュ済みのチャットメッセージを受け取ったブックマーク解除の情報に置き換え
        chatMessageService.setChatMessage({ chatMessage: recv });
        // BookmarkのViewModelからメッセージを削除
        bookmarkService.deleteBookmarkMessage({ bookmarkId: recv.id });
    }, [bookmarkService, chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.BookmarkDeleted,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== attention =====================================*/
/**
 * アテンションのメッセージ新規追加をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeAttentionItemAdded = (onRecvMessage, dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    const attentionService = useAttentionService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv attention message via websocket. ${recv.entityRecordId}`);
        // ChatMessageSliceに追加
        chatMessageService.addChatMessageToCache({
            chatMessage: recv.chatMessage,
        });
        // AttentionのViewModelに追加
        const parameter = {
            id: recv.entityRecordId,
            attentionType: recv.attentionType,
            attentionUserName: recv.actionUser.displayName,
            chatMessageId: recv.chatMessage.id,
        };
        attentionService.addAttentionMessage(parameter);
        // コールバック関数を実行。現在は既読情報の更新のみ行っている
        onRecvMessage === null || onRecvMessage === void 0 ? void 0 : onRecvMessage(recv);
    }, [attentionService, chatMessageService, onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AttentionItemAdded,
        IsValidMessage: IsAttentionItem,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * アテンションのメッセージの削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeAttentionItemDeleted = (dispatch, selector) => {
    const attentionService = useAttentionService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // AttentionのViewModelから削除
        attentionService.deleteAttentionMessage({
            attentionId: recv.entityRecordId,
        });
        // 他の同様の処理ではmessageを除去しているが、Attentionはメッセージと1:1ではないため、messageはそのまま残す
    }, [attentionService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AttentionItemDeleted,
        IsValidMessage: IsDeletedAttentionItem,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== chat_message ================================*/
/**
 * チャットのメッセージ新規追加をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeChatMessageAdded = (chatRoomId, onRecvMessage, dispatch, selector) => {
    // Sliceの操作を行うためのServiceを取得
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv message via websocket. ${recv.id}`);
        // ChatMessageSliceに追加
        chatMessageService.addChatMessageToCache({
            chatMessage: recv,
        });
        // Chatの各ViewModelにもメッセージを追加
        chatMessageService.addChatMessage({
            chatMessage: recv,
            pubsubTargetChatRoomId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        });
        // コールバック関数を実行。現在は既読情報の更新のみ行っている
        onRecvMessage === null || onRecvMessage === void 0 ? void 0 : onRecvMessage(recv);
    }, [chatMessageService, chatRoomId, onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.ChatRoom,
        targetId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        messageTypeFilter: PubSubEventType.ChatMessageAdded,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, chatRoomId !== null);
};
/**
 * チャットのメッセージ更新をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeChatMessageUpdated = (chatRoomId, dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv message via websocket. ${recv.id}`);
        // メッセージを更新
        chatMessageService.setChatMessage({ chatMessage: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.ChatRoom,
        targetId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        messageTypeFilter: PubSubEventType.ChatMessageUpdated,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, chatRoomId !== null);
};
/**
 * チャットのメッセージの削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeChatMessageDeleted = (chatRoomId, dispatch, selector) => {
    // Sliceの操作を行うためのServiceを取得
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv message via websocket. ${recv.id}`);
        // メッセージを削除
        chatMessageService.deleteChatMessage({
            chatMessageId: recv.id,
            parentChatMessageId: recv.parentChatMessageId,
            chatRoomId: recv.chatRoomId,
            parentChatRoomId: recv.chatRoom.parentChatRoomId,
        });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.ChatRoom,
        targetId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        messageTypeFilter: PubSubEventType.ChatMessageDeleted,
        IsValidMessage: IsChatMessage,
        onRecvMessage: handleRecvMessage,
    }, chatRoomId !== null);
};
/*====== chat_reaction ===========================*/
/**
 * チャットのリアクション追加をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeChatReactionAdded = (chatRoomId, dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv add reaction via websocket. ${recv.chatMessageId}`);
        // 追加されたリアクションをキャッシュに反映
        chatMessageService.insertReaction({ reaction: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.ChatRoom,
        targetId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        messageTypeFilter: PubSubEventType.ChatMessageReactionAdded,
        IsValidMessage: IsChatReaction,
        onRecvMessage: handleRecvMessage,
    }, chatRoomId !== null);
};
/**
 * チャットのリアクション削除をsubscribeするcustom hook
 * @param params
 * @returns
 */
const useSubscribeChatReactionDeleted = (chatRoomId, dispatch, selector) => {
    const chatMessageService = useChatMessageService(dispatch);
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        console.info(`[subscribeMessage] Recv delete reaction via websocket. ${recv.chatMessageId}`);
        // 削除されたリアクションをキャッシュから削除
        chatMessageService.deleteReaction({ reaction: recv });
    }, [chatMessageService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.ChatRoom,
        targetId: chatRoomId !== null && chatRoomId !== void 0 ? chatRoomId : undefined,
        messageTypeFilter: PubSubEventType.ChatMessageReactionDeleted,
        IsValidMessage: IsChatReaction,
        onRecvMessage: handleRecvMessage,
    }, chatRoomId !== null);
};
/*====== ダイレクトチャンネル ===========================*/
/**
 * ダイレクトチャンネルの追加をsubscribeするcustom hook
 * A custom hook to subscribe if a new direct channel is created
 * @param params
 * @returns
 */
const useSubscribeDirectChannelAdded = (onRecvMessage, dispatch, selector) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.DirectChannelAdded,
        IsValidMessage: IsDirectChannel,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== 未読情報 =====================================*/
/**
 * 未読情報更新をsubscribeするcustom hook
 * @returns
 */
const useSubscribeUnreadUpdated = () => {
    const unreadService = useUnreadService();
    const activeChatRoomService = useActiveChatRoomService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        recv.forEach((info) => {
            var _a, _b;
            console.info(`[subscribeMessage] Recv UpdateUnreadInfo:${JSON.stringify(info)}`);
            // state更新（未読情報を更新）
            switch (info.targetType) {
                case UnreadInfoTarget.ChatRoom:
                    activeChatRoomService.setActiveChatRoomWithUnreadCount({
                        activeChatRoom: {
                            chatRoomId: info.chatRoomId,
                            parentChatRoomId: info.parentChatRoomId,
                            referenceEntityType: info.referenceEntityType,
                            referenceEntityRecordId: info.referenceEntityRecordId,
                            isFavorite: info.isFavorite,
                            projectName: info.projectName,
                            projectScope: info.projectScope,
                            projectType: info.projectType,
                            projectVersion: info.projectVersion,
                            projectMemberCount: info.projectMemberCount,
                            favoriteDisplayOrder: info.favoriteDisplayOrder,
                            latestChatMessageCreatedAt: info.latestChatMessageCreatedAt,
                            unreadCount: info.unreadCount,
                            lastReadMessageId: (_a = info.lastReadMessageId) !== null && _a !== void 0 ? _a : undefined,
                            updateSerial: info.updateSerial,
                        },
                    });
                    break;
                case UnreadInfoTarget.ChatThread:
                    unreadService.setChatThreadUnreadCount({
                        topicId: info.entityRecordId,
                        count: info.unreadCount,
                        lastReadMessageId: (_b = info.lastReadMessageId) !== null && _b !== void 0 ? _b : undefined,
                        updateSerial: info.updateSerial,
                    });
                    break;
                default:
                    assertNever(info, 'Invalid target type');
            }
        });
    }, [activeChatRoomService, unreadService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.UnreadUpdated,
        IsValidMessage: IsUpdateUnreadInfo,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * チャットルーム単位の「すべて既読にする」実行時の未読情報更新をsubscribeするcustom hook
 * @returns
 */
const useSubscribeUnreadChatRoomAllRead = () => {
    const unreadService = useUnreadService();
    const activeChatRoomService = useActiveChatRoomService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        recv.forEach((info) => {
            var _a, _b;
            console.info(`[subscribeMessage] Recv UnreadChatRoomAllRead:${JSON.stringify(info)}`);
            // state更新（未読情報を更新）
            switch (info.targetType) {
                case UnreadInfoTarget.ChatRoom:
                    activeChatRoomService.setActiveChatRoomWithUnreadCount({
                        activeChatRoom: {
                            chatRoomId: info.chatRoomId,
                            parentChatRoomId: info.parentChatRoomId,
                            referenceEntityType: info.referenceEntityType,
                            referenceEntityRecordId: info.referenceEntityRecordId,
                            isFavorite: info.isFavorite,
                            projectName: info.projectName,
                            projectScope: info.projectScope,
                            projectType: info.projectType,
                            projectVersion: info.projectVersion,
                            projectMemberCount: info.projectMemberCount,
                            favoriteDisplayOrder: info.favoriteDisplayOrder,
                            latestChatMessageCreatedAt: info.latestChatMessageCreatedAt,
                            unreadCount: info.unreadCount,
                            lastReadMessageId: (_a = info.lastReadMessageId) !== null && _a !== void 0 ? _a : undefined,
                            updateSerial: info.updateSerial,
                        },
                    });
                    break;
                case UnreadInfoTarget.ChatThread:
                    unreadService.setChatThreadUnreadCount({
                        topicId: info.entityRecordId,
                        count: info.unreadCount,
                        lastReadMessageId: (_b = info.lastReadMessageId) !== null && _b !== void 0 ? _b : undefined,
                        updateSerial: info.updateSerial,
                    });
                    break;
                default:
                    assertNever(info, 'Invalid target type');
            }
        });
    }, [activeChatRoomService, unreadService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.UnreadChatRoomAllRead,
        IsValidMessage: IsUpdateUnreadInfo,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * ユーザーの全チャットルームの未読情報の全既読処理をsubscribeするcustom hook
 * @param dispatch reduxのdispatch
 * @param selector reduxのuseSelector
 * @returns
 */
const useSubscribeUserUnreadAllChatRead = (dispatch, selector) => {
    const unreadService = useUnreadService();
    // メッセージ受信時
    const handleRecvMessage = useCallback(() => {
        console.info(`[subscribeMessage] Recv UnreadAllChatRead`);
        unreadService.resetAllChatUnread();
    }, [unreadService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.UnreadAllChatRead,
        IsValidMessage: true,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== ユーザー在席状態 =====================================*/
/**
 * ユーザー在席状態の更新をsubscribeするcustom hook
 * @returns
 */
const useSubscribeUserPresenceUpdated = (targetUserId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Presence,
        targetType: PubSubEventTargetType.User,
        targetId: targetUserId,
        messageTypeFilter: PubSubEventType.UserPresenceUpdated,
        IsValidMessage: IsUpdatedUserPresence,
        onRecvMessage: handleRecvMessage,
    });
};
/**
 * 議事録生成中をsubscribeするcustom hook
 * @param eventId
 * @param onRecvMessage
 * @returns
 */
const useSubscribeMeetingMinutesProcessing = (eventId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback(() => {
        onRecvMessage();
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Event,
        targetId: eventId,
        messageTypeFilter: PubSubEventType.MeetingMinutesProcessing,
        IsValidMessage: true,
        onRecvMessage: handleRecvMessage,
    });
};
/**
 * 議事録作成完了をsubscribeするcustom hook
 * @param eventId
 * @param onRecvMessage
 * @returns
 */
const useSubscribeMeetingMinutesCreated = (eventId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback(() => {
        onRecvMessage();
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Event,
        targetId: eventId,
        messageTypeFilter: PubSubEventType.MeetingMinutesCreated,
        IsValidMessage: true,
        onRecvMessage: handleRecvMessage,
    });
};
/**
 * 議事録作成失敗をsubscribeするcustom hook
 * @param eventId
 * @param onRecvMessage
 * @returns
 */
const useSubscribeMeetingMinutesGenerationFailed = (eventId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback(() => {
        onRecvMessage();
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Event,
        targetId: eventId,
        messageTypeFilter: PubSubEventType.MeetingMinutesGenerationFailed,
        IsValidMessage: true,
        onRecvMessage: handleRecvMessage,
    });
};
/**
 * 議事録自動生成時のトークン上限超過をsubscribeするcustom hook
 * @param eventId
 * @param onRecvMessage
 * @returns
 */
const useSubscribeMeetingMinutesGenerationTokenLimitExceeded = (eventId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback(() => {
        onRecvMessage();
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Event,
        targetId: eventId,
        messageTypeFilter: PubSubEventType.MeetingMinutesGenerationTokenLimitExceeded,
        IsValidMessage: true,
        onRecvMessage: handleRecvMessage,
    });
};
/**
 * 録画ストリーム状態更新をsubscribeするcustom hook
 */
export const useSubscribeUpdatedMeetingRecordingStreamState = (eventId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, payload) => {
        onRecvMessage(payload);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.Event,
        targetId: eventId,
        messageTypeFilter: PubSubEventType.MeetingRecordingStreamStateUpdated,
        IsValidMessage: IsUpdatedMeetingRecordingStreamState,
        onRecvMessage: handleRecvMessage,
    }, Boolean(eventId));
};
/**
 * AIクレジットの更新をsubscribeするcustom hook
 * @param tenantId
 * @param onRecvMessage
 * @returns
 */
export const useSubscribeUpdatedAiCredit = (tenantId, onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, payload) => {
        onRecvMessage(payload);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Data,
        targetType: PubSubEventTargetType.Tenant,
        targetId: tenantId,
        messageTypeFilter: PubSubEventType.AiCreditUpdated,
        IsValidMessage: IsUpdatedAiCredit,
        onRecvMessage: handleRecvMessage,
    }, tenantId !== undefined);
};
/*====== お知らせ =====================================*/
/**
 * お知らせの追加をsubscribeするcustom hook
 * @returns
 */
const useSubscribeAnnouncementAdded = () => {
    const announcementService = useAnnouncementService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // AnnouncementSliceにお知らせを追加
        announcementService.addAnnouncement({
            announcement: recv,
        });
    }, [announcementService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AnnouncementAdded,
        IsValidMessage: IsAnnouncement,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * お知らせの削除をsubscribeするcustom hook
 * @returns
 */
const useSubscribeAnnouncementDeleted = () => {
    const announcementService = useAnnouncementService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // AnnouncementSliceからお知らせを削除
        announcementService.deleteAnnouncement({
            announcementId: recv.id,
        });
    }, [announcementService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AnnouncementDeleted,
        IsValidMessage: IsAnnouncement,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * お知らせの既読・未読をsubscribeするcustom hook
 * @returns
 */
const useSubscribeAnnouncementRead = () => {
    const announcementService = useAnnouncementService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // AnnouncementSliceのお知らせの既読状態を更新する
        announcementService.readAnnouncement({
            announcementId: recv.id,
            isRead: recv.isRead,
        });
    }, [announcementService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AnnouncementRead,
        IsValidMessage: IsAnnouncement,
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/**
 * お知らせの全件既読をsubscribeするcustom hook
 * @returns
 */
const useSubscribeAnnouncementAllRead = () => {
    const announcementService = useAnnouncementService();
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        // AnnouncementSliceのお知らせをすべて既読にする
        announcementService.allReadAnnouncement();
    }, [announcementService]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.AnnouncementAllRead,
        IsValidMessage: true, // payloadはないため常にtrue
        onRecvMessage: handleRecvMessage,
    }, true //ユーザに対するpublishは常時subscribeしたいのでtrue
    );
};
/*====== エンティティ参照 =====================================*/
// タスク関連情報作成完了をsubscribeするcustom hook
const useSubscribeTaskRelatedSuggestionCreated = (onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.RelatedSuggested,
        IsValidMessage: IsRelatedSuggestion,
        onRecvMessage: handleRecvMessage,
    });
};
// タスク関連情報作成失敗をsubscribeするcustom hook
const useSubscribeTaskRelatedSuggestionFailed = (onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.RelatedSuggestionFailed,
        IsValidMessage: IsRelatedSuggestion,
        onRecvMessage: handleRecvMessage,
    });
};
// 会議関連情報作成完了をsubscribeするcustom hook
const useSubscribeEventRelatedSuggestionCreated = (onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.RelatedSuggested,
        IsValidMessage: IsRelatedSuggestion,
        onRecvMessage: handleRecvMessage,
    });
};
// 会議関連情報作成失敗をsubscribeするcustom hook
const useSubscribeEventRelatedSuggestionFailed = (onRecvMessage) => {
    // メッセージ受信時
    const handleRecvMessage = useCallback((_, recv) => {
        onRecvMessage(recv);
    }, [onRecvMessage]);
    return useWebsocket({
        dataType: PubSubEventDataType.Meta,
        targetType: PubSubEventTargetType.User,
        messageTypeFilter: PubSubEventType.RelatedSuggestionFailed,
        IsValidMessage: IsRelatedSuggestion,
        onRecvMessage: handleRecvMessage,
    });
};
